import { useState, useEffect, useRef } from 'react'
import { Edit2, Beef, Search } from 'lucide-react'
import toast from 'react-hot-toast'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { lotesService } from '../../services/lotesService'

function calcularIdade(dataNascimento) {
  if (!dataNascimento) return '—'
  const nasc = new Date(dataNascimento)
  if (Number.isNaN(nasc.getTime())) return '—'
  const meses = Math.floor((Date.now() - nasc.getTime()) / (1000 * 60 * 60 * 24 * 30.44))
  if (meses < 0) return '—'
  if (meses < 24) return `${meses} m`
  return `${Math.floor(meses / 12)} anos`
}

const SEXO_LABEL = { M: 'Macho', F: 'Fêmea', C: 'Castrado' }

// Tela "Cadastros > Animais" — o cadastro mestre reutilizável de cavalos
// (animais_cadastro) é criado/atualizado automaticamente ao salvar animais num
// lote (ver AnimaisCavaloEditor.jsx); esta tela é só pra visualizar/pesquisar
// e corrigir dados de identidade (não cria/exclui — isso acontece via lote).
export default function AnimaisCadastroPage() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id
  const isFluxoCavalo = organizadorAtivo?.fluxo_leilao === 'comissao_cavalo'

  const [animais, setAnimais] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [tipoFiltro, setTipoFiltro] = useState('')
  const [tiposAnimal, setTiposAnimal] = useState([])
  const [racas, setRacas] = useState([])
  const [editando, setEditando] = useState(null)
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)
  const debounceRef = useRef(null)

  useEffect(() => {
    if (orgId) {
      lotesService.listarTiposAnimais(orgId).then(setTiposAnimal).catch(() => {})
      lotesService.listarRacas(orgId).then(setRacas).catch(() => {})
    }
  }, [orgId])

  useEffect(() => {
    if (!orgId) return
    setLoading(true)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      lotesService.listarAnimaisCadastro(orgId, { search, tipoAnimalId: tipoFiltro })
        .then(setAnimais)
        .catch(() => toast.error('Erro ao carregar animais'))
        .finally(() => setLoading(false))
    }, 300)
    return () => clearTimeout(debounceRef.current)
  }, [orgId, search, tipoFiltro])

  const abrirEditar = (a) => {
    setEditando(a)
    setForm({
      nome: a.nome || '',
      sexo: a.sexo || '',
      data_nascimento: a.data_nascimento ? a.data_nascimento.slice(0, 10) : '',
      pelagem: a.pelagem || '',
      peso: a.peso ?? '',
      raca_id: a.raca_id || '',
      tipo_animal_id: a.tipo_animal_id || '',
      registro: a.registro || '',
      localizacao: a.localizacao || '',
      pai: a.pai || '',
      mae: a.mae || '',
      informacoes: a.informacoes || '',
    })
  }

  const handleSalvar = async () => {
    if (!form.nome.trim()) { toast.error('Informe o nome'); return }
    setSaving(true)
    try {
      const payload = {
        ...form,
        nome: form.nome.trim(),
        peso: form.peso !== '' ? parseFloat(form.peso) : null,
        raca_id: form.raca_id || null,
        tipo_animal_id: form.tipo_animal_id || null,
        data_nascimento: form.data_nascimento || null,
      }
      await lotesService.atualizarAnimalCadastro(orgId, editando.id, payload)
      toast.success('Animal atualizado')
      setEditando(null)
      setAnimais(prev => prev.map(a => a.id === editando.id ? { ...a, ...payload } : a))
      // Recarrega pra atualizar os nomes de raça/tipo exibidos na lista
      lotesService.listarAnimaisCadastro(orgId, { search, tipoAnimalId: tipoFiltro }).then(setAnimais).catch(() => {})
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar')
    } finally { setSaving(false) }
  }

  if (!orgId) return <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">Selecione um organizador.</div>
  if (!isFluxoCavalo) return <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">Módulo disponível apenas para organizadores com fluxo Cavalo.</div>

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Beef className="w-5 h-5 text-amber-500" />
          <h1 className="text-lg font-semibold text-gray-800">Animais</h1>
        </div>
      </div>

      <p className="text-xs text-gray-400">
        Cadastro reutilizável de animais entre leilões. Novos registros são criados automaticamente ao cadastrar animais num lote — aqui você pode pesquisar e corrigir os dados de identidade.
      </p>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome..."
            className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent"
          />
        </div>
        <select
          value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm sm:w-48 focus:ring-2 focus:ring-amber-500 focus:border-transparent"
        >
          <option value="">Todos os tipos</option>
          {tiposAnimal.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
        </select>
      </div>

      {/* Lista */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-8 text-gray-400">Carregando...</div>
        ) : animais.length === 0 ? (
          <div className="text-center py-8 text-gray-400">Nenhum animal encontrado.</div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="sm:hidden divide-y divide-gray-100">
              {animais.map(a => (
                <div key={a.id} className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <span className="font-medium text-gray-800 truncate">{a.nome}</span>
                      <div className="mt-1 text-xs text-gray-500 flex flex-wrap gap-x-3 gap-y-0.5">
                        {a.tipo_animal_nome && <span className="font-medium text-amber-700">{a.tipo_animal_nome}</span>}
                        {a.sexo && <span>{SEXO_LABEL[a.sexo] || a.sexo}</span>}
                        <span>{calcularIdade(a.data_nascimento)}</span>
                        {a.raca_nome && <span>{a.raca_nome}</span>}
                      </div>
                    </div>
                    <button onClick={() => abrirEditar(a)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg flex-shrink-0">
                      <Edit2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Nome</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Tipo</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Sexo</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Idade</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Raça</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Localização</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-600">Registro</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-600">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {animais.map(a => (
                    <tr key={a.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-800">{a.nome}</td>
                      <td className="px-4 py-3 text-amber-700 font-medium">{a.tipo_animal_nome || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{SEXO_LABEL[a.sexo] || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{calcularIdade(a.data_nascimento)}</td>
                      <td className="px-4 py-3 text-gray-600">{a.raca_nome || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{a.localizacao || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{a.registro || '—'}</td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => abrirEditar(a)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg" title="Editar">
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Modal Editar */}
      {editando && form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-800">Editar Animal</h2>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs text-gray-500 font-medium mb-1">Nome *</label>
                <input type="text" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value.toUpperCase() })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent" autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 font-medium mb-1">Sexo</label>
                  <select value={form.sexo} onChange={(e) => setForm({ ...form, sexo: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent">
                    <option value="">Selecione...</option>
                    <option value="M">Macho</option>
                    <option value="F">Fêmea</option>
                    <option value="C">Castrado</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 font-medium mb-1">Nascimento</label>
                  <input type="date" value={form.data_nascimento} onChange={(e) => setForm({ ...form, data_nascimento: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 font-medium mb-1">Tipo</label>
                <select value={form.tipo_animal_id} onChange={(e) => setForm({ ...form, tipo_animal_id: e.target.value ? Number(e.target.value) : '' })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent">
                  <option value="">Selecione...</option>
                  {tiposAnimal.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 font-medium mb-1">Raça</label>
                  <select value={form.raca_id} onChange={(e) => setForm({ ...form, raca_id: e.target.value ? Number(e.target.value) : '' })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent">
                    <option value="">Selecione...</option>
                    {racas.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 font-medium mb-1">Pelagem</label>
                  <input type="text" value={form.pelagem} onChange={(e) => setForm({ ...form, pelagem: e.target.value.toUpperCase() })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 font-medium mb-1">Registro</label>
                  <input type="text" value={form.registro} onChange={(e) => setForm({ ...form, registro: e.target.value.toUpperCase() })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 font-medium mb-1">Peso (kg)</label>
                  <input type="number" value={form.peso} onChange={(e) => setForm({ ...form, peso: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 font-medium mb-1">Localização</label>
                <input type="text" value={form.localizacao} onChange={(e) => setForm({ ...form, localizacao: e.target.value.toUpperCase() })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 font-medium mb-1">Pai</label>
                  <input type="text" value={form.pai} onChange={(e) => setForm({ ...form, pai: e.target.value.toUpperCase() })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 font-medium mb-1">Mãe</label>
                  <input type="text" value={form.mae} onChange={(e) => setForm({ ...form, mae: e.target.value.toUpperCase() })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-500 font-medium mb-1">Observações</label>
                <textarea value={form.informacoes} onChange={(e) => setForm({ ...form, informacoes: e.target.value.toUpperCase() })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent" rows="2" />
              </div>
            </div>
            <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
              <button onClick={() => setEditando(null)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 font-medium">
                Cancelar
              </button>
              <button onClick={handleSalvar} disabled={saving}
                className="flex-1 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium disabled:opacity-50">
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
